/**
 * 脚本モードのリフロー（折り返し）ユーティリティ。
 *
 * 「論理テキスト（横書き形式）」を正として保存し、
 * 縦書き表示では行長に合わせたソフト改行を動的に挿入する。
 */

import {
  LINE_HEAD_PROHIBITED_CHARS,
  LINE_END_PROHIBITED_CHARS,
  INSEPARABLE_CHARS,
  breakVerticalLines,
  convertRubyToHtml,
} from "@/lib/utils/verticalLineBreak";

// eslint-disable-next-line no-irregular-whitespace
export const SCRIPT_TOGAKI_INDENT = "　　　";
// eslint-disable-next-line no-irregular-whitespace
export const SCRIPT_SERIFU_INDENT = "　";
/** 脚本モードの1行あたりの文字数（縦書き字数 / charsPerLine） */
export const SCRIPT_LINE_LENGTH = 20;
/** 脚本モードの1ページあたりの行数（lineCountPerPage / linesPerPage） */
export const SCRIPT_LINES_PER_PAGE = 20;

/** 柱（シーン見出し）挿入ボタンで挿入する記号 */
export const SCRIPT_PILLAR_SYMBOL = "○";
/**
 * フラッシュバック挿入ボタンで挿入する文字列。
 * 3字下げ（SCRIPT_TOGAKI_INDENT）+ × + 全角スペース5 + × + 全角スペース5 + × の16字。
 * SCRIPT_LINE_LENGTH（20字）未満に収まるため、Word エクスポート時（サーバ側では折り返さず
 * Word の自動折返しに任せる方式）でもこの区切り線が意図せず折り返される心配はない。
 */
// eslint-disable-next-line no-irregular-whitespace
export const SCRIPT_FLASHBACK_TEXT = "　　　×　　　　　×　　　　　×";

/**
 * フラッシュバック等の区切り線（× を全角スペースで区切った SCRIPT_FLASHBACK_TEXT 形式の行）
 * かどうかを判定する。
 *
 * 全角スペースと × のみで構成され × を1つ以上含む「ト書き字下げで始まる行」を区切り線とみなす。
 * この行は柱・空行と同様に「折り返さない独立行」として扱い、リフロー/アンリフローの対象外にする。
 * （区切り線は 20 字を超える固定レイアウトのため、ト書きとして折り返すと崩れて後続文字も巻き込むため）
 */
export function isFlashbackLine(line: string): boolean {
  // eslint-disable-next-line no-irregular-whitespace
  return line.startsWith(SCRIPT_TOGAKI_INDENT) && /^[　×]+$/u.test(line) && line.includes("×");
}

/**
 * 行頭挿入の「挿入位置」と「挿入文字列」を算出する純粋関数（DOM 非依存・テスト用）。
 *
 * 柱（○）やフラッシュバックは必ず行頭から始める必要があるため、キャレット位置ではなく
 * 「現在行の行頭」を基準に挿入する。
 *
 * インライン挿入（`block=false`。柱○など、後続の見出し文字を同じ行に続けるもの）:
 * - 行頭〜キャレットが空白（字下げ）のみ → その行の行頭に text をそのまま挿入（改行なし）
 * - 行途中に文字がある                   → キャレット直前に改行を挟んでから text を挿入
 *
 * ブロック挿入（`block=true`。フラッシュバックなど、1行を占有する固定レイアウト）:
 * - text を独立した 1 行として挿入し、キャレット以降の文字は必ず次の行へ送る
 *   （＝ text の前後に必要なだけ改行を補い、後続文字が text と同じ論理行に残らないようにする）
 *
 * @param value 対象 textarea の全文
 * @param caret 現在のキャレット位置（selectionStart）
 * @param text  挿入する文字列
 * @param options.block 1行を占有するブロック要素として挿入するか
 * @returns insertText: execCommand("insertText") に渡す文字列 / caret: 挿入前に折りたたむ選択位置
 */
export function computeLineHeadInsertion(
  value: string,
  caret: number,
  text: string,
  options: { block?: boolean } = {}
): { insertText: string; caret: number } {
  const lineStart = value.lastIndexOf("\n", caret - 1) + 1;
  const beforeCaretInLine = value.slice(lineStart, caret);
  // 全角スペース(U+3000)を含む空白のみなら trim() で空文字になる
  const beforeIsBlank = beforeCaretInLine.trim() === "";

  if (!options.block) {
    if (beforeIsBlank) return { insertText: text, caret: lineStart };
    return { insertText: `\n${text}`, caret };
  }

  // ブロック挿入: text を独立行にし、後続文字を次行へ送る
  const nextBreak = value.indexOf("\n", caret);
  const lineEnd = nextBreak === -1 ? value.length : nextBreak;
  // 字下げのみが前にある場合は行頭から挿入し、字下げごと後続扱いにする
  const insertPos = beforeIsBlank ? lineStart : caret;
  const followingText = value.slice(insertPos, lineEnd);
  const prefix = beforeIsBlank ? "" : "\n";
  // 後続に可視文字がある → 次行へ送るため改行が必要
  // 後続が無くても、直後に既存の改行(\n)が無い（＝ドキュメント末尾）場合は、
  // 挿入直後にすぐ次の行へ入力できるよう改行を補う
  const suffix = followingText.trim() !== "" || nextBreak === -1 ? "\n" : "";
  return { insertText: `${prefix}${text}${suffix}`, caret: insertPos };
}

/**
 * textarea の現在行の行頭に text を挿入する。
 *
 * `document.execCommand("insertText", ...)` を用いることで、ネイティブの undo 履歴と
 * `input` イベント（自動保存・文字数カウント・縦書きリフロー）に確実に載せる。
 * value を直接書き換えてはならない。挿入位置の算出は {@link computeLineHeadInsertion} を参照。
 */
export function insertAtLineHead(
  textarea: HTMLTextAreaElement,
  text: string,
  options: { block?: boolean } = {}
): void {
  textarea.focus();
  const caretPos = textarea.selectionStart ?? textarea.value.length;
  const { insertText, caret } = computeLineHeadInsertion(textarea.value, caretPos, text, options);
  textarea.setSelectionRange(caret, caret);
  document.execCommand("insertText", false, insertText);
}

/** 各行の開始文字オフセット（value.split("\n") の各行が value 上で始まる index）を求める。 */
function computeLineStartOffsets(lines: string[]): number[] {
  const offsets: number[] = [];
  let off = 0;
  for (let i = 0; i < lines.length; i += 1) {
    offsets.push(off);
    off += lines[i].length + 1; // +1 は "\n"
  }
  return offsets;
}

/** 文字位置 pos を含む行インデックスを返す。 */
function lineIndexAt(lines: string[], lineStarts: number[], pos: number): number {
  for (let i = 0; i < lines.length; i += 1) {
    if (pos <= lineStarts[i] + lines[i].length) return i;
  }
  return lines.length - 1;
}

/**
 * ブロック [start, end] の本文を抽出し、3字下げ付きで折り返した（reflowScriptBlock と同一形の）文字列を返す。
 * 先頭行は既存字下げを全て除去してちょうど3字下げに正規化し、続き行は reflow と同じく最大3字まで除去する。
 */
function wrapBlockAsTogaki(lines: string[], start: number, end: number): string {
  let content = "";
  for (let i = start; i <= end; i += 1) {
    const line = lines[i];
    let sp = 0;
    if (i === start) {
      while (line[sp] === SCRIPT_SERIFU_INDENT) sp += 1;
    } else {
      while (sp < SCRIPT_TOGAKI_INDENT.length && line[sp] === SCRIPT_SERIFU_INDENT) sp += 1;
    }
    let body = line.slice(sp);
    if (body.endsWith(SOFT_BREAK_MARKER)) body = body.slice(0, -SOFT_BREAK_MARKER.length);
    content += body;
  }
  return wrapWithIndent(content, SCRIPT_TOGAKI_INDENT, SCRIPT_TOGAKI_INDENT, SCRIPT_LINE_LENGTH).join("\n");
}

/**
 * カーソル行を含む段落（ブロック）を「ト書き」に変換した、折返し済みの置換テキストと範囲を算出する
 * 純粋関数（DOM 非依存・テスト用）。
 *
 * 柱やフラッシュバックのように「カーソル位置で行を分割して挿入」するのではなく、カーソルが行途中に
 * あっても行を分割せず、その段落全体をト書きにする。
 *
 * 重要: ここで返すテキストは reflowScriptBlock が出力するのと同一の「折返し済み（禁則・マーカー付き）」
 * 形にする。これにより、挿入直後に input イベントで走る reflow が「変化なし」で即返り（no-op）、
 * execCommand が1回で済む → undo 1回でボタン押下前に戻る（挿入と折返しが別々の undo になり、
 * 中間状態が見える不具合を防ぐ）。そのため:
 * - 先頭行の行頭字下げは全て取り除き、ちょうど3字下げに正規化する（1〜2字/4字以上→3字）。
 * - 続き行は reflow と同じく行頭全角スペースを最大3字まで除去する。
 * - 本文を wrapWithIndent（reflowScriptBlock と同じ折返し）で3字下げ付きに折り返す。
 *
 * 区切り線（フラッシュバック）はト書き化しない（null を返す）。
 *
 * @returns 置換範囲 [start, end) と折返し済みテキスト insertText。対象外の行では null。
 */
export function computeTogakiBlockReplacement(
  value: string,
  caret: number
): { start: number; end: number; insertText: string } | null {
  const lines = value.split("\n");
  const lineStarts = computeLineStartOffsets(lines);
  const ci = lineIndexAt(lines, lineStarts, caret);

  // 区切り線・柱はト書き化の対象外（柱は行頭固定のため字下げしない）
  if (isFlashbackLine(lines[ci]) || scriptLineType(lines[ci]) === "hashira") return null;

  // 段落（ブロック）の行範囲を求める。空行は単独行として扱う。
  let start = ci;
  let end = ci;
  if (scriptLineType(lines[ci]) !== "blank") {
    ({ start, end } = findScriptBlockRange(lines, ci));
  }

  const wrapped = wrapBlockAsTogaki(lines, start, end);
  const blockStart = lineStarts[start];
  const blockEnd = lineStarts[end] + lines[end].length;
  return { start: blockStart, end: blockEnd, insertText: wrapped };
}

/**
 * 選択範囲 [selStart, selEnd) に掛かる全ての段落を「ト書き」に変換した、折返し済みの置換テキストと範囲を
 * 算出する純粋関数（DOM 非依存・テスト用）。
 *
 * 2段落以上を選択してト書きボタンを押した場合に、選択に掛かった各段落をそれぞれト書きにするための関数。
 * - 選択の両端は、掛かっている段落（ブロック）の先頭・末尾まで自動的に広げる（段落の一部選択でも段落全体を変換）。
 * - 段落の区切り（空行・柱・フラッシュバック区切り線）はそのまま保持し、段落は結合しない（柱は行頭固定）。
 * - 各段落は computeTogakiBlockReplacement と同じ折返し済み形にするため、後続 reflow は no-op。
 *
 * @returns 置換範囲 [start, end) と、各段落をト書き化して連結した insertText。対象が無ければ null。
 */
export function computeTogakiSelectionReplacement(
  value: string,
  selStart: number,
  selEnd: number
): { start: number; end: number; insertText: string } | null {
  if (selStart === selEnd) return computeTogakiBlockReplacement(value, selStart);

  const lines = value.split("\n");
  const lineStarts = computeLineStartOffsets(lines);
  const lo = Math.min(selStart, selEnd);
  const hi = Math.max(selStart, selEnd);

  const startLine = lineIndexAt(lines, lineStarts, lo);
  let endLine = lineIndexAt(lines, lineStarts, hi);
  // 選択終端がちょうど行頭にある場合、その行の本文は選択に含まれていないので対象から外す
  if (endLine > startLine && hi === lineStarts[endLine]) endLine -= 1;

  // 空行・柱・区切り線はト書き化の対象外としてそのまま保持する（柱は行頭固定）。
  const isSeparator = (i: number) =>
    scriptLineType(lines[i]) === "blank" || scriptLineType(lines[i]) === "hashira" || isFlashbackLine(lines[i]);

  // 選択の両端を、掛かっている段落の先頭・末尾まで広げる
  const regionStart = isSeparator(startLine) ? startLine : findScriptBlockRange(lines, startLine).start;
  const regionEnd = isSeparator(endLine) ? endLine : findScriptBlockRange(lines, endLine).end;

  // 段落ごとにト書き化（区切り行はそのまま保持）
  const out: string[] = [];
  let i = regionStart;
  while (i <= regionEnd) {
    if (isSeparator(i)) {
      out.push(lines[i]);
      i += 1;
    } else {
      const { end } = findScriptBlockRange(lines, i);
      const blockEnd = Math.min(end, regionEnd);
      out.push(wrapBlockAsTogaki(lines, i, blockEnd));
      i = blockEnd + 1;
    }
  }

  const startChar = lineStarts[regionStart];
  const endChar = lineStarts[regionEnd] + lines[regionEnd].length;
  return { start: startChar, end: endChar, insertText: out.join("\n") };
}

/**
 * textarea の選択範囲（無選択ならカーソル行）を含む段落をト書き（行頭3字下げ・折返し済み）に置換する。
 * 算出は {@link computeTogakiSelectionReplacement} を参照。`document.execCommand("insertText", ...)` で
 * undo 履歴と input イベント（リフロー）に載せる。折返し済みを1回で挿入するため後続 reflow は no-op。
 *
 * @returns テキストを変更したら true。既にト書きで変更不要なら false（呼び出し側は reflow 発火も省ける）。
 */
export function insertTogakiAtLine(textarea: HTMLTextAreaElement): boolean {
  textarea.focus();
  const selStart = textarea.selectionStart ?? textarea.value.length;
  const selEnd = textarea.selectionEnd ?? selStart;
  const replacement = computeTogakiSelectionReplacement(textarea.value, selStart, selEnd);
  if (!replacement) return false;
  const { start, end, insertText } = replacement;
  // 既にト書き（＝置換後テキストが現在の範囲と同一）なら何もしない。
  // 同一内容での insertText でも undo 履歴が積まれ、ト書きから戻るのに押した回数分の undo が
  // 必要になる不具合を防ぐ。
  if (insertText === textarea.value.slice(start, end)) return false;
  textarea.setSelectionRange(start, end);
  document.execCommand("insertText", false, insertText);
  return true;
}

/*
 * 脚本ツールバーの挿入アクション（柱 / フラッシュバック / ト書き）。
 * EditorController（縦書きプレビュー）と ManuscriptEditor（本文エディタ）の両ツールバーで
 * 同一実装を共有するための関数（挙動変更時に片方だけ直す事故を防ぐ）。
 * reflow 発火は各コンポーネント側の実装（textarea へ input を dispatch）をコールバックで受け取る。
 */

/** 柱（◯）を行頭に挿入し、reflow を発火する。 */
export function insertPillarTo(textarea: HTMLTextAreaElement, dispatchInputForReflow: () => void): void {
  insertAtLineHead(textarea, SCRIPT_PILLAR_SYMBOL);
  dispatchInputForReflow();
}

/** フラッシュバック区切り線を行頭にブロック挿入し、reflow を発火する。 */
export function insertFlashbackTo(textarea: HTMLTextAreaElement, dispatchInputForReflow: () => void): void {
  // フラッシュバックは1行を占有する区切り線。後続文字を次行へ送るためブロック挿入する
  insertAtLineHead(textarea, SCRIPT_FLASHBACK_TEXT, { block: true });
  dispatchInputForReflow();
}

/**
 * 現在行をト書き（行頭3字下げ）に正規化する。カーソルが行途中にあっても行は分割しない。
 * 既にト書きで変更が無ければ reflow 発火を省く。
 */
export function insertTogakiTo(textarea: HTMLTextAreaElement, dispatchInputForReflow: () => void): void {
  if (insertTogakiAtLine(textarea)) dispatchInputForReflow();
}

/**
 * 禁則処理でソフト改行された行の末尾に付与する不可視マーカー（Word Joiner）。
 * unreflow 時にこのマーカーがある行は「字数が満たなくても続き行」と判定する。
 * 論理テキストには含まれず、表示テキストのみに存在する。
 */
export const SOFT_BREAK_MARKER = "⁠";

/**
 * ソフト改行マーカー(U+2060)を全て除去する。
 * マーカーは縦書き脚本の表示専用アーティファクトであり、永続化される論理テキスト
 * （サーバー / IndexedDB / S3）には含まれてはならない、という不変条件を保証するための関数。
 * unreflow を経由しない保存経路（マルチ執筆など）でもマーカー混入を防ぐ最終防波堤として使う。
 */
export function stripSoftBreakMarker(text: string): string {
  return text.split(SOFT_BREAK_MARKER).join("");
}

export type ScriptLineType = "blank" | "hashira" | "togaki" | "serifuCont" | "serifuHead" | "plain";

/**
 * セリフのヘッド行（名前「…）を判定する正規表現。
 * 名前部（「の直前）は空白・鉤括弧を含まない 1〜10 文字を要求する。
 * scriptImport.ts の SERIFU_START_REGEXP と同一定義。名前なしの「…」単独行や、
 * 名前でも「でもないただの文（プレーンテキスト）はセリフとみなさず plain 扱いにする。
 */
// eslint-disable-next-line no-irregular-whitespace
export const SCRIPT_SERIFU_HEAD_REGEXP = /^[^\s「」]{1,10}「/u;

/**
 * 行の種別を判定する。ト書き(3字下げ)はセリフ続き(1字下げ)より先に判定する。
 *
 * 字下げの無い行は「名前「…」の形（SCRIPT_SERIFU_HEAD_REGEXP）に一致するときだけセリフのヘッド
 * (serifuHead) とみなす。一致しない字下げ無しの行はプレーンテキスト (plain) とし、セリフとは扱わない
 * （＝続き行に1字下げを付けない・セリフブロックへ吸収しない）。
 *
 * ト書き (togaki) は「ちょうど3字下げ」のときだけ。全角スペース4字以上で始まる行は「手動で深く
 * 字下げしただけの行」とみなしてプレーンテキスト (plain 相当) 扱いにし、続き行に字下げを伝播しない
 * （4字目が全角スペースかどうかで判定。3字下げ＋本文先頭が全角スペース、というケースも同様に
 *  ト書きにはしない — その全角スペースは本文の一部）。
 */
export function scriptLineType(line: string): ScriptLineType {
  if (line.length === 0) return "blank";
  if (/^[◯○〇]/u.test(line)) return "hashira";
  // ちょうど3字下げ（4字目が全角スペースでない）のみ togaki
  if (line.startsWith(SCRIPT_TOGAKI_INDENT) && line[SCRIPT_TOGAKI_INDENT.length] !== SCRIPT_SERIFU_INDENT) {
    return "togaki";
  }
  if (line.startsWith(SCRIPT_SERIFU_INDENT)) return "serifuCont";
  if (SCRIPT_SERIFU_HEAD_REGEXP.test(line)) return "serifuHead";
  return "plain";
}

/**
 * ソフト折返し段落の「続き行」になり得る行か。
 *
 * 空行・柱・区切り線（フラッシュバック）は独立行であり、段落の続き行にはなり得ない。
 * これらをマーカー行の直後で段落へ連結してしまうと、空行の消失や柱の欠落（本文の欠落）を招く。
 * reflow 直後はマーカー行の次に必ず同一段落の続き行が来るため通常は問題にならないが、
 * 折返し済み段落を reflow せずに編集した状態（続き行を削除した後など）では
 * 「マーカー行の直後に空行・柱・区切り線」という並びが実際に発生し得るため、明示的に弾く。
 */
function isBlockContinuable(line: string): boolean {
  const type = scriptLineType(line);
  return type !== "blank" && type !== "hashira" && !isFlashbackLine(line);
}

/**
 * 縦書き脚本モードで Backspace されたときの削除範囲を算出する（DOM 非依存・テスト用）。
 *
 * 「ソフト折返しの続き行の行頭（字下げ内）」で Backspace した場合のみ、続き元（直前）の行の
 * 本文末尾1文字の削除範囲 [start, end) を返す（自然なリフロー編集）。それ以外は null を返し、
 * 呼び出し側は既定の Backspace（＝行結合。末尾の空行はこれで消える）に委ねる。
 *
 * - 続き行かどうかは「直前行が SOFT_BREAK_MARKER で終わるか」で判定する。
 *   （reflow が全てのソフト折返し行にマーカーを付与するため確実。空行・別段落・手動改行の行頭では
 *    直前行にマーカーが無いので介入せず、末尾に空行が取り残される不具合を防ぐ）
 * - 続き元の行はマーカーで終わるため、削除対象はマーカーの手前の本文1文字。マーカー自体は残し、
 *   折返しはリフローで再計算させる。
 */
export function computeScriptBackspaceDeletion(value: string, caret: number): { start: number; end: number } | null {
  if (caret === 0) return null;
  const lineStart = value.lastIndexOf("\n", caret - 1) + 1;
  if (lineStart === 0) return null; // 先頭行は続き元が無い

  // カーソルが行頭の字下げ範囲内にある場合のみ介入（本文中の削除は既定動作）
  const col = caret - lineStart;
  let indentLen = 0;
  while (value[lineStart + indentLen] === SCRIPT_SERIFU_INDENT) indentLen += 1;
  if (col > indentLen) return null;

  const prevLineStart = value.lastIndexOf("\n", lineStart - 2) + 1;
  const prevLine = value.slice(prevLineStart, lineStart - 1);
  // ソフト折返しの続き元でなければ介入しない（既定の Backspace に任せて行結合させる）
  if (!prevLine.endsWith(SOFT_BREAK_MARKER)) return null;

  const prevVisible = prevLine.slice(0, -SOFT_BREAK_MARKER.length);
  const prevChars = Array.from(prevVisible);
  if (prevChars.length === 0) return null;
  const lastChar = prevChars[prevChars.length - 1];
  // マーカーの手前にある本文末尾1文字を削除する（マーカーは残す）
  const end = lineStart - 1 - SOFT_BREAK_MARKER.length;
  const start = end - lastChar.length;
  return { start, end };
}

export type ScriptBlockType = "togaki" | "serifu" | "hashira" | "plain";

/**
 * カーソル行 `ci` を含む「同一段落（ブロック）」の行範囲 [start, end] と種別を求める（DOM 非依存・テスト用）。
 *
 * ■ 範囲 [start, end] の決め方
 * ソフト折返しの続き行かどうかは「直前行が折返しマーカー(SOFT_BREAK_MARKER)で終わるか」だけで判定し、
 * 文字数でも、隣接行の「現在の」字下げ種別（scriptLineType）でも判定しない。これにより次の誤結合を防ぐ:
 * - 20字ちょうどの手動改行行を続き行と誤認して、改行直後の新しい行を前段落へ吸収する
 * - ト書き字下げのために打ち始めた全角スペース1つを「セリフ続き行」と誤認して前のセリフ行へ吸収する
 * マーカーは reflow 自身がこのブロックを折り返した際にしか付与されないため、マーカーの有無だけで
 * 「この2行は元々同一段落だったか」を確実に判定できる。
 *
 * ■ blockType（続き行の字下げ幅）の決め方
 * blockType は「カーソル行」ではなく「ブロックの先頭行 lines[start]」の種別から決める。
 * 続き行の字下げ幅は段落全体の性質であり、段落の頭で決まるべきだから。カーソル行基準にすると、
 * 種別変更の途中（例: ト書き段落の続き行の字下げをまだ消していない）でカーソルが続き行にあるだけで
 * 段落全体の字下げ幅が揺れてしまう。先頭行基準なら、続き行の古い字下げは reflow が先頭行の幅へ
 * 自己修復する。
 * - 先頭が3字下げ(togaki)          → togaki（続き行も3字下げ）
 * - 先頭が名前「…(serifuHead)       → serifu（続き行は1字下げ）
 * - それ以外（プレーンテキスト、手動で1〜2字下げしただけの行）→ plain（続き行は字下げなし＝フラット）
 *   ※ 1〜2字下げは「セリフ続き」ではなく単なる手動字下げとみなし、続き行に字下げを伝播しない。
 *     3字揃って初めてト書きとして続き行にも字下げが付く。
 *
 * 呼び出し側は `lines[ci]` が blank / フラッシュバック区切り線でないことを保証すること。
 */
export function findScriptBlockRange(lines: string[], ci: number): { start: number; end: number; blockType: ScriptBlockType } {
  const isSoftWrapSource = (line: string) => line.endsWith(SOFT_BREAK_MARKER);
  const curType = scriptLineType(lines[ci]);
  let start = ci;
  let end = ci;

  if (curType === "hashira") {
    // 柱は単一行として扱う
    return { start, end, blockType: "hashira" };
  }

  // ソフト折返しの続き行をマーカー基準で前後へ広げる。
  // 空行・柱・区切り線は続き行になり得ないため連結しない（吸収すると空行消失・柱の欠落を招く）。
  // ※ 後方は lines[start - 1] がマーカーで終わる場合のみ拡張するが、空行・柱・区切り線は
  //   マーカーを持たないため isSoftWrapSource で自然に弾かれる。
  while (start > 0 && isSoftWrapSource(lines[start - 1]) && isBlockContinuable(lines[start - 1])) start -= 1;
  while (end < lines.length - 1 && isSoftWrapSource(lines[end]) && isBlockContinuable(lines[end + 1])) end += 1;

  // blockType は先頭行の種別で決める（続き行の現在の字下げには依存しない）。
  const headType = scriptLineType(lines[start]);
  let blockType: ScriptBlockType;
  if (headType === "togaki") {
    blockType = "togaki";
  } else if (headType === "serifuHead") {
    blockType = "serifu";
  } else {
    // plain / serifuCont(＝手動で1〜2字下げしただけの行) は続き行を字下げしないフラット段落
    blockType = "plain";
  }
  return { start, end, blockType };
}

/**
 * カーソル位置を含む脚本ブロックを、全文 split せず文字オフセットで求める。
 * findScriptBlockRange と同じ範囲・種別。空行・区切り線では null。
 */
export function findScriptBlockCharRange(
  text: string,
  caret: number
): { start: number; end: number; blockType: ScriptBlockType } | null {
  const clamped = Math.max(0, Math.min(caret, text.length));
  const lineStart = clamped === 0 ? 0 : text.lastIndexOf("\n", clamped - 1) + 1;
  const lineNl = text.indexOf("\n", clamped);
  const lineEnd = lineNl === -1 ? text.length : lineNl;
  const currentLine = text.slice(lineStart, lineEnd);
  const curType = scriptLineType(currentLine);

  if (curType === "blank" || isFlashbackLine(currentLine)) return null;
  if (curType === "hashira") {
    return { start: lineStart, end: lineEnd, blockType: "hashira" };
  }

  let start = lineStart;
  let end = lineEnd;

  while (start > 0) {
    const prevEnd = start - 1;
    const prevStart = text.lastIndexOf("\n", start - 2) + 1;
    const prevLine = text.slice(prevStart, prevEnd);
    if (prevLine.endsWith(SOFT_BREAK_MARKER) && isBlockContinuable(prevLine)) {
      start = prevStart;
    } else {
      break;
    }
  }

  while (end < text.length) {
    const lastLineStart = end === 0 ? 0 : text.lastIndexOf("\n", end - 1) + 1;
    const lastLine = text.slice(lastLineStart, end);
    if (!lastLine.endsWith(SOFT_BREAK_MARKER)) break;
    const nextStart = end + 1;
    const nextNl = text.indexOf("\n", nextStart);
    const nextEnd = nextNl === -1 ? text.length : nextNl;
    const nextLine = text.slice(nextStart, nextEnd);
    if (!isBlockContinuable(nextLine)) break;
    end = nextEnd;
  }

  const headNl = text.indexOf("\n", start);
  const headEnd = headNl === -1 || headNl > end ? end : headNl;
  const headType = scriptLineType(text.slice(start, headEnd));
  let blockType: ScriptBlockType;
  if (headType === "togaki") {
    blockType = "togaki";
  } else if (headType === "serifuHead") {
    blockType = "serifu";
  } else {
    blockType = "plain";
  }

  return { start, end, blockType };
}

/**
 * 本文テキストを指定行長で折り返す。
 *
 * @param content 字下げを含まない本文
 * @param headIndent 先頭行の字下げ
 * @param contIndent 続き行の字下げ
 * @param lineLength 1行あたりの最大文字数（字下げ含む）
 * @returns 折り返された行の配列
 */
export function wrapWithIndent(content: string, headIndent: string, contIndent: string, lineLength: number): string[] {
  if (content.length === 0) return [headIndent];

  const contentChars = Array.from(content);
  const headIndentChars = Array.from(headIndent);
  const contIndentChars = Array.from(contIndent);
  const contIndentLen = contIndentChars.length;
  const maxPushOut = Math.floor(lineLength / 2);

  const lines: string[] = [];
  let lineChars: string[] = [...headIndentChars];
  let count = headIndentChars.length;
  let currentIndentLen = headIndentChars.length;
  let i = 0;

  while (i < contentChars.length) {
    if (count + 1 > lineLength) {
      // 禁則処理（追い出し方式）: 行頭禁則・行末禁則・分離禁止に対応する
      let pushOut = 0;
      while (pushOut < maxPushOut && lineChars.length > currentIndentLen) {
        const tailChar = lineChars[lineChars.length - 1];
        const headChar = contentChars[i] ?? "";

        const needPushOut =
          LINE_HEAD_PROHIBITED_CHARS.includes(headChar) ||
          LINE_END_PROHIBITED_CHARS.includes(tailChar) ||
          (INSEPARABLE_CHARS.includes(tailChar) && headChar === tailChar);

        if (!needPushOut) break;

        lineChars.pop();
        count -= 1;
        i -= 1;
        pushOut += 1;
      }

      // 折り返し（ソフト改行）した行の末尾には必ずマーカーを付与する。
      // これにより unreflow / 編集時のブロック判定が「ソフト改行の続き行」と「手動改行の別段落」を
      // 行の文字数に依存せず確実に区別できる（満杯20字に依存すると、削除で20字を割った瞬間に
      // 続き行を見失い、字下げのみの空行が取り残される不具合が起きるため）。
      lines.push(lineChars.join("") + SOFT_BREAK_MARKER);
      lineChars = [...contIndentChars];
      count = contIndentLen;
      currentIndentLen = contIndentLen;
    }

    lineChars.push(contentChars[i]);
    count += 1;
    i += 1;
  }

  if (lineChars.length > 0) lines.push(lineChars.join(""));
  return lines;
}

/**
 * 論理テキスト（横書き形式）を縦書き表示テキストに変換する。
 * 各論理行を lineLength 文字で折り返し、適切な字下げを付与する。
 */
export function reflowLogicalToVertical(
  logicalText: string,
  lineLength: number = SCRIPT_LINE_LENGTH,
  addGuard: boolean = true
): string {
  const inputLines = logicalText.split("\n");
  const result: string[] = [];

  inputLines.forEach((line) => {
    const type = scriptLineType(line);

    if (type === "blank" || type === "hashira" || isFlashbackLine(line)) {
      // 空行・柱・区切り線（フラッシュバック）は折り返さずそのまま出力する
      result.push(line);
    } else if (type === "togaki") {
      const content = line.slice(SCRIPT_TOGAKI_INDENT.length);
      const wrapped = wrapWithIndent(content, SCRIPT_TOGAKI_INDENT, SCRIPT_TOGAKI_INDENT, lineLength);
      // ガード: 最終行がちょうど lineLength 文字の場合、末尾に全角スペースを付加して
      // unreflow 時にソフト改行と誤判定されないようにする
      if (addGuard) {
        const lastIdx = wrapped.length - 1;
        if (Array.from(wrapped[lastIdx]).length === lineLength) {
          wrapped[lastIdx] += "\u3000"; // 全角スペース
        }
      }
      result.push(...wrapped);
    } else if (type === "serifuHead") {
      const wrapped = wrapWithIndent(line, "", SCRIPT_SERIFU_INDENT, lineLength);
      result.push(...wrapped);
    } else {
      // plain（プレーンテキスト）と serifuCont（手動で1〜2字下げしただけの行）はフラットに折り返す。
      // 論理テキストにはソフト折返しの続き行が無いため、行頭の字下げは本文としてそのまま保持し、
      // 続き行には字下げを付けない（ヘッド・続き行とも字下げなし）。
      const wrapped = wrapWithIndent(line, "", "", lineLength);
      result.push(...wrapped);
    }
  });

  return result.join("\n");
}

/**
 * 縦書き表示テキスト（ソフト改行を含む）から論理テキスト（横書き形式）に戻す。
 * 「行が lineLength 文字で満杯」かつ「次行も同一ブロック」→ ソフト改行として結合する。
 */
export function unreflowVerticalToLogical(verticalText: string, lineLength: number = SCRIPT_LINE_LENGTH): string {
  const lines = verticalText.split("\n");
  const result: string[] = [];
  let i = 0;

  while (i < lines.length) {
    // 区切り線（フラッシュバック）は独立行として扱い、ト書き連結の対象にしない
    // （ト書き字数判定では満杯行に見えるため、明示的に先に弾く）
    if (isFlashbackLine(lines[i])) {
      result.push(lines[i]);
      i += 1;
      // eslint-disable-next-line no-continue
      continue;
    }

    const type = scriptLineType(lines[i]);

    if (type === "blank" || type === "hashira") {
      result.push(lines[i]);
      i += 1;
    } else if (type === "togaki") {
      // 同一段落のト書き行を連結。
      // ソフト折返し判定は「折返しマーカーで終わるか」のみで行う（reflow が全ソフト折返しに付与）。
      // 文字数（20字ちょうど等）では判定しない。20字の手動改行行を続き行と誤認すると、
      // 保存（unreflow）時に別段落が結合されてしまうため。
      // ※ 旧データにガードスペース(末尾全角)が残っていてもマーカー無し＝連結対象外として扱い、
      //   末尾の全角スペースだけ除去する（マーカーは長さを持つので長さ判定はマーカー除去後で行う）。
      const stripMarker = (line: string) =>
        line.endsWith(SOFT_BREAK_MARKER) ? line.slice(0, -SOFT_BREAK_MARKER.length) : line;
      const extractTogakiContent = (line: string) => stripMarker(line).slice(SCRIPT_TOGAKI_INDENT.length);
      const visibleLen = (line: string) => Array.from(stripMarker(line)).length;
      const isSoftBreakLine = (line: string) => line.endsWith(SOFT_BREAK_MARKER);

      // 連結は「現在行が折返しマーカーで終わるか」だけで見分ける（種別では見ない）。
      // ト書きの続き行の本文がたまたま全角スペースで始まると 4字下げに見えて scriptLineType が
      // togaki でなくなるため、種別一致を要求すると続き行が分断されてしまう。マーカーは reflow が
      // 同一段落のソフト折返し行にしか付けず最終行には付かないので、これで過不足なく連結できる。
      let merged = extractTogakiContent(lines[i]);
      // 空行・柱・区切り線は続き行にならないため連結しない（柱を吸収すると本文が欠落する）。
      while (i < lines.length - 1 && isSoftBreakLine(lines[i]) && isBlockContinuable(lines[i + 1])) {
        i += 1;
        merged += extractTogakiContent(lines[i]);
      }
      // ガードスペースの除去: 旧 reflow が付加した末尾の全角スペース（可視長 lineLength+1）を取り除く
      if (visibleLen(lines[i]) === lineLength + 1) {
        merged = merged.replace(/\u3000$/, "");
      }
      result.push(SCRIPT_TOGAKI_INDENT + merged);
      i += 1;
    } else if (type === "serifuHead") {
      // セリフブロック: ヘッド行（名前「…）＋続き行(1字下げ=serifuCont)を連結し、続き行の字下げを除去する。
      const stripMarker = (s: string) => (s.endsWith(SOFT_BREAK_MARKER) ? s.slice(0, -SOFT_BREAK_MARKER.length) : s);

      let merged = stripMarker(lines[i]);
      while (i < lines.length - 1 && scriptLineType(lines[i + 1]) === "serifuCont") {
        i += 1;
        merged += stripMarker(lines[i].slice(SCRIPT_SERIFU_INDENT.length));
      }
      result.push(merged);
      i += 1;
    } else {
      // プレーンテキスト(plain) と、手動で1〜2字下げしただけの行(serifuCont が段落先頭に来たケース)。
      // どちらも続き行に字下げが付かないフラット段落なので、ソフト折返しの続き行を
      // 「現在行が折返しマーカーで終わるか」だけで見分けて連結する（字下げの除去は不要）。
      // ・続き行の本文がたまたま「名前「…」の形でも serifuHead と誤判定して分断しない。
      // ・マーカーは段落内のソフト折返し行にしか付かず最終行には付かないため、マーカーの連鎖が
      //   別段落へまたがることはない（手動改行の隣接行は連結されない）。
      // ・先頭行の行頭字下げ（手動の1〜2字下げ）は本文として保持する（除去しない）。
      const stripMarker = (s: string) => (s.endsWith(SOFT_BREAK_MARKER) ? s.slice(0, -SOFT_BREAK_MARKER.length) : s);
      let merged = stripMarker(lines[i]);
      // 空行・柱・区切り線は続き行にならないため連結しない（吸収すると空行消失・柱の欠落を招く）。
      while (i < lines.length - 1 && lines[i].endsWith(SOFT_BREAK_MARKER) && isBlockContinuable(lines[i + 1])) {
        i += 1;
        merged += stripMarker(lines[i]);
      }
      result.push(merged);
      i += 1;
    }
  }

  return result.join("\n");
}

/**
 * 脚本モードのページ数（P換算）を算出する。
 *
 * 論理テキスト（横書き形式）を段落ごとに breakVerticalLines（追い出し禁則つき）で行分割し、
 * 行数合計を linesPerPage で割って切り上げる。プレビュー / PDF / Word 出力（いずれも
 * breakVerticalLines を使用）とページ数を揃えるため、reflow の `\n` 数ではなくこの方式で数える。
 *
 * @param logicalText 論理テキスト。縦書き表示中の表示テキストは unreflow してから渡すこと。
 * @param lineLength 1行あたりの文字数（既定: 脚本モードの 20）
 * @param linesPerPage 1ページあたりの行数（既定: 脚本モードの 20）
 */
export function countScriptPages(
  logicalText: string,
  lineLength: number = SCRIPT_LINE_LENGTH,
  linesPerPage: number = SCRIPT_LINES_PER_PAGE
): number {
  let lineCount = 0;
  logicalText.split("\n").forEach((paragraph) => {
    // 空段落（空行）も1行として数える（プレビュー実装と同じ扱い）
    lineCount += breakVerticalLines(convertRubyToHtml(paragraph), lineLength).length || 1;
  });
  return Math.ceil(lineCount / linesPerPage);
}
